import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import UserAccount from '../../services/UserAccount';
import UserAvatar from '../UserAvatar';
import Popup from '../Popup/index';
import {
  AnalyticsIcon,
  AppsIcon,
  BellIcon,
  CalendarIcon,
  CompanyIcon,
  DeleteIcon,
  FileIcon,
  FlagIcon,
  Grid9Icon,
  HomeIcon,
  InfoIcon,
  LeaveIcon,
  NoteIcon,
  SettingsIcon,
  SubmissionsIcon,
  UsersIcon,
} from '../../styledhtml/Icon';

const MENU_ICON = { width: 16, height: 16, flexShrink: 0 };
/** Mask icons already follow `DarkTextColor` — invert only black PNG/SVG glyphs. */
const MASK_MENU_ICONS = new Set([InfoIcon, NoteIcon]);
const menuIconStyle = (Icon) => (
  MASK_MENU_ICONS.has(Icon) || !Constants.isDarkMode
    ? MENU_ICON
    : { ...MENU_ICON, filter: 'invert(1)' }
);

const Panel = styled.div`
  width: 15.75rem;
  padding: 0.3rem;
  box-sizing: border-box;
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem 0.5rem;
  margin-bottom: 0.15rem;
  border: none;
  border-radius: 14px;
  background:
    linear-gradient(125deg, rgba(30, 189, 83, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%),
    ${Constants.SurfaceColor};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s, transform 0.1s;

  &:hover {
    background:
      linear-gradient(125deg, rgba(30, 189, 83, 0.12) 0%, rgba(139, 92, 246, 0.1) 100%),
      ${Constants.SurfaceColor};
    transform: translateY(-1px);
  }
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1;
`;

const HeaderName = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${Constants.DarkTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderMeta = styled.div`
  margin-top: 0.05rem;
  font-size: 0.75rem;
  color: ${Constants.MediumTextGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.08rem;
  padding: 0.1rem 0;
`;

const GroupLabel = styled.div`
  padding: 0.25rem 0.5rem 0.1rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${Constants.LightTextGray};
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.4rem 0.5rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: ${(p) => (p.$danger ? '#b42318' : Constants.DarkTextColor)};
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: ${(p) => (p.$danger ? 'rgba(180, 35, 24, 0.08)' : Constants.SurfaceHover)};
  }
`;

const RowIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${(p) => (p.$danger ? 'rgba(180, 35, 24, 0.08)' : Constants.SurfaceHover)};
  color: ${(p) => (p.$danger ? '#b42318' : Constants.DarkTextColor)};
  flex-shrink: 0;
`;

const RowLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
`;

const Divider = styled.div`
  height: 1px;
  margin: 0.12rem 0.45rem;
  background: ${Constants.HairlineBorderRgba};
`;

function MenuRow({ Icon, label, onClick, danger }) {
  return (
    <Row type="button" onClick={onClick} $danger={danger}>
      <RowIcon $danger={danger}>
        <Icon style={menuIconStyle(Icon)} />
      </RowIcon>
      <RowLabel>{label}</RowLabel>
    </Row>
  );
}

MenuRow.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  danger: PropTypes.bool,
};

function renderSections(sections, go) {
  return sections.map((section, index) => {
    const rows = section.rows.filter(Boolean);
    if (rows.length === 0) return null;
    return (
      <React.Fragment key={section.label || `section-${index}`}>
        {index > 0 && <Divider />}
        <Group>
          {section.label && <GroupLabel>{section.label}</GroupLabel>}
          {rows.map(({ Icon, label, action, danger }) => (
            <MenuRow
              key={label}
              Icon={Icon}
              label={label}
              danger={danger}
              onClick={() => go(action)}
            />
          ))}
        </Group>
      </React.Fragment>
    );
  });
}

export function getStudioAccountSections({ isAuthorizedToLogIn }) {
  return [
    {
      rows: [
        {
          Icon: SettingsIcon,
          label: 'Settings',
          action: () => Navigation.push(Navigation.accountSettings),
        },
      ],
    },
    {
      label: 'Studio',
      rows: [
        {
          Icon: SubmissionsIcon,
          label: 'Submissions',
          action: () => Navigation.push('/?content=submissions'),
        },
        {
          Icon: DeleteIcon,
          label: 'Trash',
          action: () => Navigation.push(`/?content=${!isAuthorizedToLogIn && 'submissions' || 'content'}&state=deleted`),
        },
      ],
    },
    {
      rows: [
        {
          Icon: HomeIcon,
          label: 'Platform',
          action: () => { window.location = 'https://logictry.com'; },
        },
      ],
    },
    {
      rows: [
        {
          Icon: LeaveIcon,
          label: 'Log out',
          danger: true,
          action: () => UserAccount.logout(),
        },
      ],
    },
  ];
}

export function getLogicBaseAccountSections({ isDevAccount, openNotifications }) {
  return [
    {
      rows: [
        {
          Icon: SettingsIcon,
          label: 'Settings',
          action: () => Navigation.push(Navigation.accountSettings),
        },
      ],
    },
    {
      rows: [
        isDevAccount && {
          Icon: AnalyticsIcon,
          label: 'Analytics',
          action: () => Navigation.push(Navigation.analytics),
        },
        isDevAccount && {
          Icon: InfoIcon,
          label: 'Errors & perf',
          action: () => Navigation.push(Navigation.observabilityErrors),
        },
        {
          Icon: BellIcon,
          label: 'Notifications',
          action: openNotifications,
        },
      ],
    },
    {
      rows: [
        {
          Icon: UsersIcon,
          label: 'Logic Circle',
          action: () => Navigation.push(Navigation.logicCircle),
        },
        {
          Icon: FlagIcon,
          label: 'Chapters',
          action: () => Navigation.push(Navigation.chapters),
        },
        {
          Icon: CalendarIcon,
          label: 'Convenings',
          action: () => Navigation.push(Navigation.convenings),
        },
        {
          Icon: NoteIcon,
          label: 'Content',
          action: () => Navigation.push(Navigation.content),
        },
        {
          Icon: Grid9Icon,
          label: 'Collections',
          action: () => Navigation.push(Navigation.collections),
        },
        {
          Icon: CompanyIcon,
          label: 'Company',
          action: () => Navigation.push(Navigation.company),
        },
      ],
    },
    {
      rows: [
        {
          Icon: AppsIcon,
          label: 'Creator Studio',
          action: () => { window.location = 'https://studio.logictry.com'; },
        },
        {
          Icon: FileIcon,
          label: 'Legal',
          action: () => Navigation.push(Navigation.legal),
        },
      ],
    },
    {
      rows: [
        {
          Icon: LeaveIcon,
          label: 'Log out',
          danger: true,
          action: () => UserAccount.logout(),
        },
      ],
    },
  ];
}

export default function AccountMenu({
  open,
  anchorEl,
  account,
  onClose,
  sections,
  anchorBottomRight,
  profileOnMainSite,
}) {
  if (!open || !account) return null;

  const displayName = account.fullname || account.username || 'Your account';
  const handle = account.username ? `@${account.username}` : account.email || 'View profile';

  const go = (action) => {
    onClose();
    if (action) action(account);
  };

  const openProfile = (user) => {
    if (profileOnMainSite) window.location = Navigation.mainSiteUserProfile(user);
    else Navigation.push(Navigation.userProfile(user));
  };

  return (
    <Popup
      allowBackgroundClose
      onClose={onClose}
      anchorEl={anchorEl}
      anchorBottomRight={anchorBottomRight}
      anchorBorderRadius={18}
    >
      <Panel>
        <Header
          type="button"
          onClick={() => go(openProfile)}
        >
          <UserAvatar user={account} widthAndHeight={36} showShadow />
          <HeaderText>
            <HeaderName>{displayName}</HeaderName>
            <HeaderMeta>{handle}</HeaderMeta>
          </HeaderText>
        </Header>

        {renderSections(sections, go)}
      </Panel>
    </Popup>
  );
}

AccountMenu.propTypes = {
  open: PropTypes.bool,
  anchorEl: PropTypes.object,
  account: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    rows: PropTypes.arrayOf(PropTypes.shape({
      Icon: PropTypes.elementType,
      label: PropTypes.string,
      action: PropTypes.func,
      danger: PropTypes.bool,
    })),
  })).isRequired,
  anchorBottomRight: PropTypes.bool,
  profileOnMainSite: PropTypes.bool,
};
